// Request DTO builders for the Playbid backend (docs/telemetry/03 and 04, as
// implemented by the platform's `/api/v1/*`, `/v1/tracking/relay` and
// `/v1/adbreaks/resolve`).
//
// Every function here takes plain summaries and returns exactly the documented
// request body. No internal SDK object (AdSession, VastResponse, AdBreak,
// PlaybackSession) is ever passed through - the same rule the SDK enforces at
// its Publisher Event boundary (ADR-021).
//
// Pure and total: they never read global state and never throw, so a malformed
// input cannot break the playback path that calls them. Ids and clocks are
// passed in, which is what makes a retried request byte-identical to its
// first attempt (the backend deduplicates on those ids).

export const SCHEMA_VERSION = '1.0';

// POST /api/v1/events
export function buildEventBatch({ batchId, publisherId, sentAt, events }) {
  return {
    schemaVersion: SCHEMA_VERSION,
    batchId,
    publisherId,
    sentAt,
    events: Array.isArray(events) ? events : [],
  };
}

// POST /api/v1/heartbeat - page-level liveness, one row per site.
export function buildHeartbeat({ beaconId, publisherId, sentAt, sdkVersion, uptimeMs, widgets }) {
  return {
    schemaVersion: SCHEMA_VERSION,
    beaconId,
    publisherId,
    sentAt,
    sdkVersion: sdkVersion || '0.0.0',
    uptimeMs: Math.max(0, Math.floor(numberOr(uptimeMs, 0))),
    activeWidgets: (Array.isArray(widgets) ? widgets : [])
      .filter((w) => w?.widgetId && w?.widgetType && w?.sessionId)
      .map((w) => ({
        widgetId: String(w.widgetId),
        widgetType: w.widgetType,
        state: String(w.state || 'UNKNOWN'),
        sessionId: String(w.sessionId),
      })),
  };
}

// POST /api/v1/errors - one failure, richer than the event that also counts it.
export function buildErrorReport({ errorId, publisherId, error, context, timestamp }) {
  const code = typeof error?.code === 'string' && error.code ? error.code : 'SDK_UNKNOWN_ERROR';
  return {
    schemaVersion: SCHEMA_VERSION,
    errorId,
    publisherId,
    code,
    category: ERROR_CATEGORY[error?.category] ?? 'ERROR',
    component: String(error?.subsystem ?? error?.context?.subsystem ?? error?.name ?? 'sdk').slice(
      0,
      256,
    ),
    // Recoverable means the widget carried on (content played, next ad tried);
    // the backend only accepts WARN/ERROR/CRITICAL here.
    severity: error?.recoverable === false ? 'CRITICAL' : 'ERROR',
    recoverable: error?.recoverable !== false,
    retryable: error?.retryable === true,
    message: String(error?.message ?? 'Unknown error').slice(0, 1024),
    stackTrace: null,
    context,
    timestamp: numberOr(timestamp, Date.now()),
  };
}

// The SDK's ErrorCategory values, projected onto the backend's closed enum.
const ERROR_CATEGORY = Object.freeze({
  NETWORK: 'ERROR',
  VAST: 'ADVERTISEMENT',
  WRAPPER: 'ADVERTISEMENT',
  DISPLAY: 'DISPLAY',
  PLAYBACK: 'PLAYBACK',
  TRACKING: 'TRACKING',
  CONFIGURATION: 'SYSTEM',
  LIFECYCLE: 'WIDGET',
  API: 'SYSTEM',
});

// POST /v1/tracking/relay - a record of our own tracker delivery attempt.
export function buildTrackingRelayRequest({ apiKey, requestId, event, url, outcome, reason }) {
  const relay = {
    event: event ?? 'unknown',
    url: url ?? null,
    outcome: outcome === 'failed' ? 'failed' : 'sent',
  };
  if (relay.outcome === 'failed' && reason) relay.reason = String(reason).slice(0, 64);
  return { apiKey, requestId: requestId ?? null, relays: [relay] };
}

// POST /v1/adbreaks/resolve
export function buildAdBreakResolveRequest({
  apiKey,
  contentId,
  contentDurationSec,
  adSlots,
  midRollPositionsSec,
}) {
  const body = { apiKey };
  if (contentId) body.contentId = contentId;
  if (typeof contentDurationSec === 'number' && Number.isFinite(contentDurationSec)) {
    body.contentDurationSec = contentDurationSec;
  }
  if (Array.isArray(adSlots) && adSlots.length) body.adSlots = adSlots;
  if (Array.isArray(midRollPositionsSec) && midRollPositionsSec.length) {
    body.midRollPositionsSec = midRollPositionsSec;
  }
  return body;
}

function numberOr(value, fallback) {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}
